import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import { drawWaveform, downsamplePeaks, peaksFromAudioBuffer, PITCH } from "../lib/waveform"
import { VoiceCapture } from "../models/voice_capture"
import { VoicePlayback } from "../models/voice_playback"
import { formatDuration } from "../helpers/timing_helper"

const WAVEFORM_BINS = 64

// Ported from Beep's recorder (VoiceCapture + VoicePlayback + canvas wave). The take is recorded,
// can be paused and listened to, and then goes through Active Storage direct upload; the form
// carries the signed blob id, the length and the wave so the player draws without decoding.
//
// States: idle → recording ⇄ paused → review → uploading. Elements say where they belong with
// data-voice-recorder-show="state other-state"; everything else is hidden.
export default class extends Controller {
  static targets = [ "canvas", "timer", "previewWave", "time", "notice", "noticeText", "playToggle",
                     "pauseToggle", "fileInput", "form", "blobField", "durationField", "progress" ]
  static values = { directUploadUrl: String }

  #capture = null
  #peaks = []
  #previewPeaks = []
  #raf = null
  #playbackRaf = null
  #timerId = null
  #seconds = 0
  #blob = null
  #fileName = null
  #objectUrl = null
  #playback = null
  #buildingPartial = false
  #partialTimeout = null
  #sendWhenStopped = false

  connect() {
    this.#setState("idle")
  }

  disconnect() {
    this.#teardown()
  }

  async start() {
    this.#capture ||= new VoiceCapture({
      onData: () => { if (this.#buildingPartial) this.#enterPaused() },
      onStop: () => this.#onStopped()
    })
    if (this.#capture.busy) return
    if (!this.#capture.available) {
      this.#showNotice("O microfone só funciona em conexão segura (HTTPS).")
      return
    }

    this.#resetTake()
    this.#hideNotice()
    this.#setState("recording")
    this.#renderTimer()

    const refused = await this.#capture.open()
    if (refused) {
      this.#setState("idle")
      this.#showNotice(refused === "NotAllowedError"
        ? "Permissão de microfone negada. Libere o acesso no navegador e tente de novo."
        : "Não foi possível acessar o microfone.")
      return
    }

    // Discarding while the permission prompt was open resolves here later: without this the
    // microphone would open on a screen already at rest, and stay open.
    if (this.#state !== "recording") {
      this.#capture.release()
      return
    }

    this.#capture.start()
    this.#startTimer()
    this.#drawLive()
  }

  // Pausing hands back what was recorded so far, so the take can be heard before carrying on.
  togglePause() {
    if (this.#state === "paused") return this.#continueTake()
    if (this.#state !== "recording" || !this.#capture?.recording) return

    this.#stopTimer()
    this.#stopDraw()
    this.#buildingPartial = true
    this.#capture.pauseAfterFlush()
    this.#partialTimeout = setTimeout(() => this.#enterPaused(), 250)
  }

  stop() {
    if (this.#state === "recording" || this.#state === "paused") this.#capture?.stop()
  }

  discard() {
    this.#teardown()
    this.#setState("idle")
  }

  async togglePlayback() {
    if (this.#playback?.playing) {
      this.#playback.pause()
      return
    }
    this.#markPlaying(true)
    try {
      await this.#playback?.play()
      this.#startPlaybackLoop()
    } catch {
      this.#markPlaying(false)
      this.#showNotice("Não foi possível reproduzir este áudio neste navegador.")
    }
  }

  async seek(event) {
    const duration = this.#duration
    if (!duration) return
    const rect = this.previewWaveTarget.getBoundingClientRect()
    const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1)
    await this.#playback?.seek(ratio * duration)
    this.#renderPreview(ratio)
    this.#showTime(ratio * duration)
  }

  // A file from the device goes through the same review and upload as a recording.
  async pick(event) {
    const file = event.target.files?.[0]
    if (!file) return

    this.#teardown()
    this.#hideNotice()
    this.#blob = file
    this.#fileName = file.name
    try {
      const buffer = await new (window.AudioContext || window.webkitAudioContext)()
        .decodeAudioData(await file.arrayBuffer())
      this.#previewPeaks = peaksFromAudioBuffer(buffer, WAVEFORM_BINS)
      this.#seconds = Math.round(buffer.duration)
    } catch {
      this.#previewPeaks = []
      this.#seconds = 0
    }
    this.#setState("review")
    this.#buildPreview()
  }

  send() {
    if (this.#state === "recording" || this.#state === "paused") {
      this.#sendWhenStopped = true
      this.#capture.stop()
    } else if (this.#state === "review") {
      this.#upload()
    }
  }

  #continueTake() {
    if (!this.#capture?.paused) return
    this.#stopPlaybackLoop()
    this.#markPlaying(false)
    this.#resetPreviewAudio()
    this.#blob = null
    this.#setState("recording")
    this.#capture.resume()
    this.#startTimer()
    this.#drawLive()
  }

  #enterPaused() {
    if (!this.#buildingPartial) return
    this.#buildingPartial = false
    clearTimeout(this.#partialTimeout)
    this.#blob = this.#capture.recorded()
    this.#previewPeaks = downsamplePeaks(this.#peaks, WAVEFORM_BINS)
    this.#setState("paused")
    this.#buildPreview()
  }

  #onStopped() {
    this.#buildingPartial = false
    clearTimeout(this.#partialTimeout)
    this.#stopTimer()
    this.#stopDraw()
    this.#blob = this.#capture.recorded()
    this.#fileName = this.#capture.mimeType.includes("mp4") ? "gravacao.m4a" : "gravacao.webm"
    this.#previewPeaks = downsamplePeaks(this.#peaks, WAVEFORM_BINS)
    this.#capture.release()
    this.#capture = null
    this.#setState("review")
    this.#buildPreview()
    if (this.#sendWhenStopped) this.#upload()
  }

  #upload() {
    if (!this.#blob) return
    this.#sendWhenStopped = false
    this.#playback?.pause()
    this.#setState("uploading")
    this.#showProgress(0)

    // "audio/webm;codecs=opus" has to travel as "audio/webm": the storage PUT compares the blob's
    // type with the request's Content-Type, which Rails reads without parameters, and refuses it.
    const type = (this.#blob.type || "audio/webm").split(";")[0]
    const file = this.#blob instanceof File ? this.#blob : new File([ this.#blob ], this.#fileName, { type })
    const delegate = {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.total) this.#showProgress((event.loaded / event.total) * 100)
        })
      }
    }

    new DirectUpload(file, this.directUploadUrlValue, delegate).create((error, blob) => {
      if (error) {
        this.#setState("review")
        this.#showNotice("Não foi possível enviar o áudio. Tente de novo.")
        return
      }
      this.blobFieldTarget.value = blob.signed_id
      this.durationFieldTarget.value = String(this.#seconds)
      this.#writeWaveform()
      this.formTarget.requestSubmit()
    })
  }

  #writeWaveform() {
    this.formTarget.querySelectorAll("[data-waveform-bin]").forEach((input) => input.remove())
    this.#previewPeaks.forEach((value) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "audio[waveform][]"
      input.value = String(Math.round(value * 100))
      input.dataset.waveformBin = ""
      this.formTarget.appendChild(input)
    })
  }

  #buildPreview() {
    this.#resetPreviewAudio()
    this.#objectUrl = URL.createObjectURL(this.#blob)
    this.#playback = new VoicePlayback(this.#objectUrl, {
      onEnd: () => this.#playbackEnded(),
      onPause: () => this.#playbackStopped()
    })
    requestAnimationFrame(() => this.#renderPreview(0))
    this.#showTime(this.#seconds)
  }

  #playbackStopped() {
    this.#markPlaying(false)
    this.#stopPlaybackLoop()
  }

  #playbackEnded() {
    this.#playbackStopped()
    this.#renderPreview(0)
    this.#showTime(this.#duration)
  }

  #startPlaybackLoop() {
    this.#stopPlaybackLoop()
    const tick = () => {
      const duration = this.#duration
      if (duration && this.#playback) {
        this.#renderPreview(this.#playback.currentTime / duration)
        this.#showTime(Math.min(this.#playback.currentTime, duration))
      }
      this.#playbackRaf = requestAnimationFrame(tick)
    }
    this.#playbackRaf = requestAnimationFrame(tick)
  }

  #stopPlaybackLoop() {
    if (this.#playbackRaf) {
      cancelAnimationFrame(this.#playbackRaf)
      this.#playbackRaf = null
    }
  }

  #renderPreview(progress) {
    drawWaveform(this.previewWaveTarget, this.#previewPeaks, progress)
  }

  #drawLive() {
    const barIntervalMs = 100
    let pendingPeak = 0
    let lastBarAt = performance.now() - barIntervalMs

    const tick = () => {
      if (!this.#capture) return
      pendingPeak = Math.max(pendingPeak, this.#capture.level())

      const now = performance.now()
      if (now - lastBarAt >= barIntervalMs) {
        this.#peaks.push(pendingPeak)
        pendingPeak = 0
        lastBarAt = now
      }

      const fraction = Math.min((now - lastBarAt) / barIntervalMs, 1)
      const live = this.#peaks.map((peak) => Math.min(1, peak * 2.2))
      drawWaveform(this.canvasTarget, live, 1, { align: "right", offsetX: fraction * PITCH })

      this.#raf = requestAnimationFrame(tick)
    }
    tick()
  }

  #stopDraw() {
    if (this.#raf) {
      cancelAnimationFrame(this.#raf)
      this.#raf = null
    }
  }

  #startTimer() {
    this.#renderTimer()
    this.#timerId = setInterval(() => {
      this.#seconds += 1
      this.#renderTimer()
    }, 1000)
  }

  #stopTimer() {
    if (this.#timerId) {
      clearInterval(this.#timerId)
      this.#timerId = null
    }
  }

  #renderTimer() {
    this.timerTarget.textContent = formatDuration(this.#seconds)
  }

  #showTime(seconds) {
    this.timeTarget.textContent = formatDuration(seconds)
  }

  #showProgress(percentage) {
    this.progressTarget.textContent = `Enviando áudio… ${Math.round(percentage)}%`
  }

  #markPlaying(on) {
    this.playToggleTarget.setAttribute("aria-pressed", String(on))
    this.playToggleTarget.setAttribute("aria-label", on ? "Pausar gravação" : "Ouvir gravação")
    this.element.toggleAttribute("data-voice-recorder-playing", on)
  }

  #resetTake() {
    this.#peaks = []
    this.#previewPeaks = []
    this.#seconds = 0
    this.#blob = null
    this.#sendWhenStopped = false
    this.#resetPreviewAudio()
  }

  #teardown() {
    this.#buildingPartial = false
    clearTimeout(this.#partialTimeout)
    this.#stopTimer()
    this.#stopDraw()
    this.#stopPlaybackLoop()
    this.#capture?.release()
    this.#capture = null
    this.#resetTake()
    if (this.hasFileInputTarget) this.fileInputTarget.value = ""
    if (this.hasPlayToggleTarget) this.#markPlaying(false)
  }

  #resetPreviewAudio() {
    this.#playback?.release()
    this.#playback = null
    if (this.#objectUrl) {
      URL.revokeObjectURL(this.#objectUrl)
      this.#objectUrl = null
    }
  }

  #showNotice(text) {
    this.noticeTextTarget.textContent = text
    this.noticeTarget.hidden = false
  }

  #hideNotice() {
    this.noticeTarget.hidden = true
  }

  get #duration() {
    return this.#playback?.duration || this.#seconds
  }

  get #state() {
    return this.element.dataset.voiceRecorderState || "idle"
  }

  #setState(state) {
    this.element.dataset.voiceRecorderState = state
    this.element.querySelectorAll("[data-voice-recorder-show]").forEach((element) => {
      element.hidden = !element.dataset.voiceRecorderShow.split(" ").includes(state)
    })
    if (this.hasPauseToggleTarget) {
      this.pauseToggleTarget.setAttribute("aria-label", state === "paused" ? "Continuar gravando" : "Pausar gravação")
      this.pauseToggleTarget.setAttribute("aria-pressed", String(state === "paused"))
    }
  }
}
